import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Send, Loader2, Star, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_REVIEW_URL = "https://g.page/r/CdFVvohyjpRZEAI/review";

export default function QuoteSection() {
  const [form, setForm] = useState({ name: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/quotes`, form);
      if (typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-18393712619/OGwqCIqNl-YcEOuP58JE",
          transaction_id: data.id || "",
        });
      }
      toast.success("Quote request sent! We'll call you back shortly.");
      setForm({ name: "", phone: "", service: "", message: "" });
      setSubmitted(true);
    } catch (err) {
      toast.error("Something went wrong. Please call us at 416-508-6931.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="quote-section" id="quote" className="py-24 sm:py-32 px-6 lg:px-8 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#F59E0B] mb-4">Free Quote</p>
          <h2 className="font-display uppercase tracking-tight text-4xl sm:text-5xl mb-6">Ready When You Are.</h2>
          <p className="text-neutral-400 leading-relaxed mb-10 max-w-md">
            Tell us what you need cleaned, hauled or plowed. We'll get back to you with a fair, no-pressure quote — usually the same day.
          </p>
          <a data-testid="quote-call-button" href="tel:+14165086931" className="inline-flex items-center gap-3 border border-white/20 px-6 py-4 hover:border-[#F59E0B] transition-colors duration-200">
            <Phone className="w-5 h-5 text-[#F59E0B]" strokeWidth={2.5} />
            <span className="font-display text-2xl tracking-wider">416-508-6931</span>
          </a>
          <p className="text-neutral-500 text-sm mt-4">Open 9am – 7pm, 7 days a week.</p>
        </div>
        {submitted ? (
          <div data-testid="quote-success-card" className="bg-[#171717] border border-[#F59E0B]/40 p-8 flex flex-col items-start gap-5">
            <CheckCircle2 className="w-12 h-12 text-[#F59E0B]" strokeWidth={1.5} />
            <h3 className="font-display uppercase tracking-tight text-3xl">Request Received!</h3>
            <p className="text-neutral-300 leading-relaxed">
              Thanks — we'll call you back shortly with your free quote.
            </p>
            <p className="text-neutral-400 text-sm leading-relaxed">
              Happy with our work? A quick Google review helps our small business more than you know.
            </p>
            <a
              data-testid="quote-success-review-button"
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-[#F59E0B] text-[#F59E0B] font-display uppercase tracking-wider text-xl px-6 py-3 hover:bg-[#F59E0B] hover:text-black transition-colors duration-200 active:scale-95"
            >
              <Star className="w-5 h-5 fill-current" /> Leave a Google Review
            </a>
            <button
              data-testid="quote-another-button"
              onClick={() => setSubmitted(false)}
              className="text-xs tracking-[0.2em] uppercase font-bold text-neutral-500 hover:text-[#F59E0B] transition-colors duration-200"
            >
              Send another request
            </button>
          </div>
        ) : (
        <form data-testid="quote-form" onSubmit={submit} className="bg-[#171717] border border-white/10 p-8 flex flex-col gap-5">
          <div>
            <label htmlFor="quote-name" className="text-xs tracking-[0.2em] uppercase font-bold text-neutral-400 block mb-2">Name *</label>
            <Input
              id="quote-name"
              data-testid="quote-name-input"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Your name"
              className="bg-[#0A0A0A] border-white/20 rounded-none p-4 h-auto focus-visible:ring-[#F59E0B]"
            />
          </div>
          <div>
            <label htmlFor="quote-phone" className="text-xs tracking-[0.2em] uppercase font-bold text-neutral-400 block mb-2">Phone *</label>
            <Input
              id="quote-phone"
              data-testid="quote-phone-input"
              required
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="416-555-1234"
              className="bg-[#0A0A0A] border-white/20 rounded-none p-4 h-auto focus-visible:ring-[#F59E0B]"
            />
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase font-bold text-neutral-400 block mb-2">Service Needed *</label>
            <Select required value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
              <SelectTrigger data-testid="quote-service-select" className="bg-[#0A0A0A] border-white/20 rounded-none p-4 h-auto focus:ring-[#F59E0B]">
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent className="bg-[#171717] border-white/20 text-white rounded-none">
                <SelectItem data-testid="service-option-power-washing" value="Power Washing">Power Washing</SelectItem>
                <SelectItem data-testid="service-option-junk-removal" value="Junk Removal">Junk Removal</SelectItem>
                <SelectItem data-testid="service-option-snow-plowing" value="Snow Plowing">Snow Plowing</SelectItem>
                <SelectItem data-testid="service-option-snow-contract" value="Snow Plowing — Season Contract ($800–$1,200)">Snow Plowing — Season Contract ($800–$1,200)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label htmlFor="quote-message" className="text-xs tracking-[0.2em] uppercase font-bold text-neutral-400 block mb-2">Details (optional)</label>
            <Textarea
              id="quote-message"
              data-testid="quote-message-input"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Tell us about the job — size, location, timing..."
              rows={4}
              className="bg-[#0A0A0A] border-white/20 rounded-none p-4 focus-visible:ring-[#F59E0B]"
            />
          </div>
          <button
            data-testid="quote-submit-button"
            type="submit"
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 bg-[#F59E0B] text-black font-display uppercase tracking-wider text-2xl px-8 py-4 hover:bg-[#D97706] transition-colors duration-200 active:scale-95 disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" strokeWidth={2.5} />}
            {loading ? "Sending..." : "Request Free Quote"}
          </button>
        </form>
        )}
      </div>
    </section>
  );
}
